import { specialtyContent } from "./specialty-content";
import { slugifySpecialty } from "./specialty-slug";

export const PULSEBOARD_SITE_URL = "https://canberra.pulseboard.com.au";

export interface SpecialistSeoInput {
  slug: string;
  name: string;
  specialty: string | null | undefined;
  suburb?: string | null;
  practice?: string | null;
  phone?: string | null;
  website?: string | null;
  addressLine?: string | null;
  /** Street line only (no suburb/state/postcode) — for PostalAddress. */
  street?: string | null;
  postcode?: string | null;
  /** Published focus tags (sub-specialties / clinical interests). */
  focus?: string[] | null;
  /** True when the practice has published referral details. */
  hasReferral?: boolean;
  cityName?: string;
}

export interface SpecialtySeoInput {
  slug: string;
  name: string;
  count: number;
  cityName?: string;
  specialists?: Array<{ slug: string; name: string; suburb?: string | null }>;
}

export interface SeoMetadata {
  title: string;
  description: string;
  path: string;
  canonical: string;
}

export interface SpecialistSeoMetadata extends SeoMetadata {
  /** Data-poor profiles stay noindex,follow until they are useful (audit report 28). */
  noindex: boolean;
}

/** "Private Practice", "Private Practice, Canberra" — import placeholders, not practices (report 73). */
export function isPlaceholderPractice(value: string | null | undefined): boolean {
  return /^private practice\b/i.test(value?.trim() ?? "");
}

export function cleanPracticeName(value: string | null | undefined): string | undefined {
  const cleaned = cleanText(value);
  return cleaned && !isPlaceholderPractice(cleaned) ? cleaned : undefined;
}

/**
 * Indexing threshold for profiles (report 28): noindex when there is no
 * street address AND none of phone, focus tags or referral details. A suburb
 * on its own is not an address (report 86).
 */
export function isDataPoorProfile(input: SpecialistSeoInput): boolean {
  const hasAddress = Boolean(cleanText(input.street));
  const hasPhone = Boolean(cleanText(input.phone));
  const hasFocus = (input.focus ?? []).some((tag) => Boolean(cleanText(tag)));
  return !hasAddress && !hasPhone && !hasFocus && !input.hasReferral;
}

/** Published referral details on an availability row (method, email, fax or notes). */
export function hasPublishedReferral(
  availability:
    | {
        referralMethod?: string | null;
        referralEmail?: string | null;
        referralFax?: string | null;
        referralNotes?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!availability) return false;
  return [availability.referralMethod, availability.referralEmail, availability.referralFax, availability.referralNotes].some(
    (value) => Boolean(cleanText(value)),
  );
}

/** Home › Directory › {Specialty heading} › {Name} (report 28: profiles had no breadcrumb). */
export function specialistBreadcrumbs(
  input: { slug: string; name: string; specialty: string | null | undefined },
  cityName = "Canberra",
): Array<{ name: string; path: string }> {
  const specialtyName = cleanText(input.specialty);
  const specialtySlug = specialtyName ? slugifySpecialty(specialtyName) : undefined;
  return [
    { name: "Home", path: "/" },
    { name: "Directory", path: "/directory" },
    ...(specialtyName && specialtySlug
      ? [{ name: specialtyHeading(specialtySlug, specialtyName, cityName), path: `/directory/${specialtySlug}` }]
      : []),
    { name: input.name, path: `/directory/specialist/${input.slug}` },
  ];
}

/** "an ENT surgeon", "an orthopaedic surgeon", "a cardiologist". */
export function withArticle(word: string): string {
  return `${/^(ENT|MRI|ECG|EMG)\b/.test(word) || /^[aeiou]/i.test(word) ? "an" : "a"} ${word}`;
}

/** Practitioner noun for a specialty name; falls back without lower-casing acronyms. */
export function practitionerFor(specialty: string | null | undefined): string {
  const name = cleanText(specialty);
  if (!name) return "specialist";
  const content = specialtyContent(slugifySpecialty(name));
  if (content) return content.practitioner;
  // Keep acronyms ("ENT") intact; lower-case ordinary words only.
  const words = name.split(/\s+/).map((word) => (/^[A-Z]{2,}\b/.test(word) ? word : word.toLowerCase()));
  return `${words.join(" ")} specialist`;
}

export interface AccessReportStructuredDataInput {
  slug: string;
  title: string;
  issueNo: number;
  publishedAt: string;
  period: { from: string; to: string };
  data: { city: string };
  narrative: { intro: string; keyFindings: string[] };
}

/**
 * Specialty pages with fewer listings than this are thin: they stay live for
 * users but are noindex,follow and left out of the sitemap until they fill up
 * (SEO audit report 14). Shared by the SPA <SEO> and the build-time prerender.
 */
export const THIN_SPECIALTY_MIN_LISTINGS = 3;

export function isThinSpecialty(count: number): boolean {
  return count < THIN_SPECIALTY_MIN_LISTINGS;
}

export function canonicalUrl(path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${PULSEBOARD_SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
}

export function specialistSeoMetadata(input: SpecialistSeoInput): SpecialistSeoMetadata {
  const cityName = input.cityName ?? "Canberra";
  const practitioner = practitionerFor(input.specialty);
  const Practitioner = practitioner.charAt(0).toUpperCase() + practitioner.slice(1);
  const practice = cleanPracticeName(input.practice);
  const suburb = cleanText(input.suburb);
  const place = suburb && suburb.toLowerCase() !== "canberra" ? `${suburb} ACT` : cityName;
  const path = `/directory/specialist/${input.slug}`;

  // Title: "Dr Jane Doe — Cardiologist, Deakin ACT | PulseBoard", shortened
  // step by step (never cut mid-word) to stay within 60 characters.
  const title =
    [
      `${input.name} — ${Practitioner}, ${place} | PulseBoard`,
      `${input.name} — ${Practitioner}, ${place}`,
      `${input.name} — ${Practitioner}, ${cityName}`,
      `${input.name} — ${Practitioner}`,
    ].find((candidate) => candidate.length <= 60) ?? `${input.name} — ${Practitioner}`;

  // Description: built from parts and fitted by dropping parts, never by
  // truncating. No availability claims (none is published for most profiles).
  const lead = `${input.name}, ${practitioner}${practice ? ` at ${practice}` : ""}${suburb ? `, ${suburb === "Canberra" ? "Canberra City" : suburb}` : ""}, ${cityName}.`;
  const focus = (input.focus ?? []).map((tag) => tag.trim()).filter(Boolean);
  const focusPart = focus.length ? ` Interests: ${focus.slice(0, 3).join(", ")}.` : "";
  const tail = input.phone || input.street ? " Practice address and contact details for referrals." : " Practice details for referrals.";
  const description =
    [`${lead}${focusPart}${tail}`, `${lead}${focusPart}`, `${lead}${tail}`, lead].find(
      (candidate) => candidate.length <= 160,
    ) ?? truncateDescription(lead);

  return {
    title,
    description,
    path,
    canonical: canonicalUrl(path),
    noindex: isDataPoorProfile(input),
  };
}

export function specialtySeoMetadata(input: SpecialtySeoInput): SeoMetadata {
  const cityName = input.cityName ?? "Canberra";
  const path = `/directory/${input.slug}`;
  const content = specialtyContent(input.slug);
  // Practitioner nouns match how patients search ("cardiologist Canberra");
  // no availability claims unless availability is actually published
  // (SEO audit report 09).
  const plural = content?.practitionerPlural ?? `${input.name} specialists`;
  const singular = content?.practitioner ?? `${input.name.toLowerCase()} specialist`;
  const heading = `${capitalise(plural)} in ${cityName}`;
  const base = `${heading} — ${input.count} listed`;
  const branded = `${base} | PulseBoard`;

  return {
    title: branded.length <= 60 ? branded : base,
    description: truncateDescription(
      `Find ${articleFor(singular)} ${singular} in ${cityName}: ${input.count} listed with practice addresses and contact details, plus how referrals, Medicare and costs work.`,
    ),
    path,
    canonical: canonicalUrl(path),
  };
}

/** "Cardiologists in Canberra" — the visible H1 for a specialty page. */
export function specialtyHeading(slug: string, name: string, cityName = "Canberra"): string {
  const plural = specialtyContent(slug)?.practitionerPlural;
  return plural ? `${capitalise(plural)} in ${cityName}` : `${name} in ${cityName}`;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function articleFor(word: string): "a" | "an" {
  // Initialisms read letter-by-letter ("an ENT surgeon"); otherwise by vowel.
  if (/^(ENT|MRI|ECG)\b/.test(word)) return "an";
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

export function briefArchiveSeoMetadata(): SeoMetadata {
  const path = "/brief";
  return {
    title: "Canberra Referral Brief · PulseBoard",
    description:
      "Read the latest Canberra Referral Brief: weekly specialist availability changes, new listings and current public-health alerts.",
    path,
    canonical: canonicalUrl(path),
  };
}

export function accessReportSeoMetadata(
  issue?: AccessReportStructuredDataInput,
): SeoMetadata {
  const path = issue ? `/access-report/${issue.slug}` : "/access-report";
  const description = issue
    ? truncateDescription(
        cleanText(issue.narrative.keyFindings[0]) ??
          cleanText(issue.narrative.intro) ??
          `Quarterly data on specialist access in ${issue.data.city}.`,
      )
    : "PulseBoard's quarterly, citable public dataset on specialist findability, live availability coverage and reported wait times in Canberra.";

  return {
    title: issue ? `${issue.title} · PulseBoard` : "Canberra Specialist Access Report · PulseBoard",
    description,
    path,
    canonical: canonicalUrl(path),
  };
}

/** schema.org MedicalSpecialty enumeration members, keyed by specialty slug. */
const SCHEMA_SPECIALTY: Record<string, string> = {
  cardiology: "Cardiovascular",
  dermatology: "Dermatology",
  endocrinology: "Endocrine",
  "ent-ear-nose-and-throat": "Otolaryngologic",
  "gastroenterology-and-hepatology": "Gastroenterologic",
  "geriatrics-general-medicine": "Geriatric",
  haematology: "Hematologic",
  "infectious-diseases": "Infectious",
  "medical-oncology": "Oncologic",
  nephrology: "Renal",
  neurology: "Neurologic",
  neurosurgery: "Surgical",
  "orthopaedic-surgery": "Musculoskeletal",
  "plastic-and-reconstructive-surgery": "PlasticSurgery",
  psychiatry: "Psychiatric",
  rheumatology: "Rheumatologic",
  urology: "Urologic",
  "vascular-surgery": "Surgical",
};

export function buildPhysicianStructuredData(
  input: SpecialistSeoInput,
): Record<string, unknown> {
  const specialtyName = cleanText(input.specialty);
  const enumMember = specialtyName ? SCHEMA_SPECIALTY[slugifySpecialty(specialtyName)] : undefined;
  const suburb = cleanText(input.suburb);
  const address = compactObject({
    "@type": "PostalAddress",
    // Street line only; locality and postcode are separate properties
    // (report 28: the old single string repeated suburb/state/postcode).
    streetAddress: cleanText(input.street) ?? (input.street === undefined ? cleanText(input.addressLine) : undefined),
    addressLocality: suburb && suburb.toLowerCase() !== "canberra" ? suburb : "Canberra",
    addressRegion: "ACT",
    postalCode: cleanText(input.postcode),
    addressCountry: "AU",
  });
  const practice = cleanPracticeName(input.practice);
  const website = cleanText(input.website);
  const focus = (input.focus ?? []).map((tag) => tag.trim()).filter(Boolean);

  return compactObject({
    "@context": "https://schema.org",
    "@type": "Physician",
    name: input.name,
    medicalSpecialty: enumMember ? `https://schema.org/${enumMember}` : specialtyName,
    address,
    telephone: cleanText(input.phone),
    url: canonicalUrl(`/directory/specialist/${input.slug}`),
    knowsAbout: focus.length ? focus : undefined,
    // The listed website is the practice's, not the doctor's own identity,
    // so it belongs on worksFor rather than sameAs (report 28).
    worksFor: practice
      ? compactObject({ "@type": "MedicalOrganization", name: practice, url: website })
      : undefined,
  });
}

export function buildSpecialtyCollectionStructuredData(
  input: SpecialtySeoInput,
): Record<string, unknown> {
  const metadata = specialtySeoMetadata(input);
  const specialists = input.specialists ?? [];

  return compactObject({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${input.name} in ${input.cityName ?? "Canberra"}`,
    description: metadata.description,
    url: metadata.canonical,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: input.count,
      itemListElement: specialists.map((specialist, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: canonicalUrl(`/directory/specialist/${specialist.slug}`),
        item: compactObject({
          "@type": "Physician",
          name: specialist.name,
          url: canonicalUrl(`/directory/specialist/${specialist.slug}`),
          medicalSpecialty: SCHEMA_SPECIALTY[input.slug]
            ? `https://schema.org/${SCHEMA_SPECIALTY[input.slug]}`
            : input.name,
          address: cleanText(specialist.suburb)
            ? { "@type": "PostalAddress", addressLocality: cleanText(specialist.suburb), addressRegion: "ACT", addressCountry: "AU" }
            : undefined,
        }),
      })),
    },
  });
}

export function buildBriefArchiveStructuredData(): Record<string, unknown> {
  const metadata = briefArchiveSeoMetadata();
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Canberra Referral Brief",
    description: metadata.description,
    url: metadata.canonical,
  };
}

export function buildAccessReportStructuredData(
  issue?: AccessReportStructuredDataInput,
): Record<string, unknown> {
  const metadata = accessReportSeoMetadata(issue);
  if (!issue) {
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Canberra Specialist Access Report",
      description: metadata.description,
      url: metadata.canonical,
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: issue.title,
    description: metadata.description,
    url: metadata.canonical,
    license: "https://creativecommons.org/licenses/by/4.0/",
    temporalCoverage: `${issue.period.from}/${issue.period.to}`,
    spatialCoverage: {
      "@type": "Place",
      name: issue.data.city || "Canberra",
    },
    creator: {
      "@type": "Organization",
      name: "PulseBoard",
    },
    datePublished: issue.publishedAt,
  };
}

function cleanText(value: string | null | undefined): string | undefined {
  const cleaned = value?.trim();
  return cleaned || undefined;
}

function truncateDescription(value: string, maxLength = 160): string {
  if (value.length <= maxLength) return value;
  const shortened = value.slice(0, maxLength - 1);
  const lastSpace = shortened.lastIndexOf(" ");
  return `${shortened.slice(0, lastSpace > 110 ? lastSpace : shortened.length).trimEnd()}…`;
}

function compactObject(
  value: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null),
  );
}
